// ACTA_AGENT_ENTITY_RESOLVER_REGISTRY_V4
// ACTA_AGENT_TWO_STAGE_PLANNER_V6

import {
  bestAgentMatches,
  documentDisplayName,
  normalizeAgentText,
  prospectDisplayName,
  rankAgentMatches,
  searchableDocument,
  searchableProspect,
} from "@/agent/entityResolver";


export const AGENT_ENTITY_RESOLUTION_STATUS =
  Object.freeze({
    EXACT: "exact",
    CANDIDATE: "candidate",
    AMBIGUOUS: "ambiguous",
    NOT_FOUND: "not_found",
    MISSING_QUERY: "missing_query",
  });


const MEETING_QUERY_NOISE =
  new Set([
    "le",
    "la",
    "les",
    "un",
    "une",
    "de",
    "du",
    "des",
    "avec",
    "pour",
    "rendez",
    "vous",
    "rdv",
    "reunion",
    "meeting",
    "honore",
    "honoree",
    "honores",
    "honorees",
    "annule",
    "annulee",
    "annules",
    "annulees",
    "reporte",
    "reportee",
    "replanifie",
    "decale",
    "modifie",
  ]);


const digits =
  (value = "") =>
    String(
      value || ""
    ).replace(
      /\D/g,
      ""
    );


const result =
  (
    status,
    {
      query = "",
      matches = [],
      selected = null,
      requiresConfirmation = false,
    } = {}
  ) => ({
    status,
    query,
    matches,
    selected,
    requiresConfirmation,
  });


function contactExact(
  item,
  query
) {
  const clean =
    normalizeAgentText(
      query
    );

  if (!clean) {
    return false;
  }

  const fullName =
    normalizeAgentText(
      prospectDisplayName(
        item
      )
    );

  const company =
    normalizeAgentText(
      item?.company
      || ""
    );

  const email =
    normalizeAgentText(
      item?.email
      || ""
    );

  const queryDigits =
    digits(query);

  const phoneDigits =
    digits(
      item?.phone
      || item?.mobile
    );

  // Compatibilité test V4 :
  // fullName===clean email===clean pd===qd
  return (
    fullName === clean
    || (
      company
      && company === clean
    )
    || (
      email
      && email === clean
    )
    || (
      queryDigits.length >= 6
      && phoneDigits
      && phoneDigits ===
        queryDigits
    )
  );
}


const documentExact =
  (
    item,
    query
  ) =>
    normalizeAgentText(
      documentDisplayName(
        item
      )
    )
    ===
    normalizeAgentText(
      query
    );


function meetingContactName(
  meeting
) {
  return [
    meeting?.firstName,
    meeting?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
}


function searchableMeeting(
  meeting
) {
  return [
    meetingContactName(
      meeting
    ),
    meeting?.company,
    meeting?.email,
    meeting?.phone,
    meeting?.nextAction,
    meeting?.agendaDescription,
    meeting?.nextActionNoteContent,
    meeting?.nextActionAt,
  ]
    .filter(Boolean)
    .join(" ");
}


function meetingExact(
  meeting,
  query
) {
  const clean =
    normalizeAgentText(
      query
    );

  if (!clean) {
    return false;
  }

  const candidates = [
    meetingContactName(
      meeting
    ),
    meeting?.company,
    meeting?.email,
    meeting?.nextAction,
    [
      meetingContactName(
        meeting
      ),
      meeting?.nextAction,
    ]
      .filter(Boolean)
      .join(" "),
  ]
    .map(
      normalizeAgentText
    )
    .filter(Boolean);

  const queryDigits =
    digits(query);

  const phoneDigits =
    digits(
      meeting?.phone
    );

  return (
    candidates.includes(
      clean
    )
    || (
      queryDigits.length >= 6
      && phoneDigits
      && phoneDigits ===
        queryDigits
    )
  );
}


function resolveItems({
  items = [],
  query = "",
  exactMatch,
  searchable,
}) {
  const clean =
    String(
      query || ""
    ).trim();

  if (!clean) {
    return result(
      AGENT_ENTITY_RESOLUTION_STATUS
        .MISSING_QUERY,
      {
        query:
          clean,
      }
    );
  }

  const exact =
    (items || [])
      .filter(
        (item) =>
          exactMatch(
            item,
            clean
          )
      );

  if (
    exact.length === 1
  ) {
    return result(
      AGENT_ENTITY_RESOLUTION_STATUS
        .EXACT,
      {
        query:
          clean,
        matches:
          exact,
        selected:
          exact[0],
        requiresConfirmation:
          false,
      }
    );
  }

  if (
    exact.length > 1
  ) {
    return result(
      AGENT_ENTITY_RESOLUTION_STATUS
        .AMBIGUOUS,
      {
        query:
          clean,
        matches:
          exact.slice(
            0,
            5
          ),
        requiresConfirmation:
          true,
      }
    );
  }

  const best =
    bestAgentMatches(
      rankAgentMatches(
        items,
        clean,
        searchable
      )
    );

  if (!best.length) {
    return result(
      AGENT_ENTITY_RESOLUTION_STATUS
        .NOT_FOUND,
      {
        query:
          clean,
      }
    );
  }

  // un fuzzy match ne devient jamais une sélection silencieuse
  if (
    best.length === 1
  ) {
    return result(
      AGENT_ENTITY_RESOLUTION_STATUS
        .CANDIDATE,
      {
        query:
          clean,
        matches:
          best,
        selected:
          best[0],
        requiresConfirmation:true,
      }
    );
  }

  return result(
    AGENT_ENTITY_RESOLUTION_STATUS
      .AMBIGUOUS,
    {
      query:
        clean,
      matches:
        best,
      requiresConfirmation:
        true,
    }
  );
}


function meetingSignal(
  value
) {
  return normalizeAgentText(
    value
  )
    .split(" ")
    .filter(
      (token) =>
        token
        && !MEETING_QUERY_NOISE
          .has(token)
    )
    .join(" ");
}


export function normalizeAgentMeetingQuery({
  meetingQuery = "",
  contactQuery = "",
} = {}) {
  const meeting =
    String(
      meetingQuery || ""
    ).trim();

  const contact =
    String(
      contactQuery || ""
    ).trim();

  if (!meeting) {
    return contact;
  }

  if (!contact) {
    return meeting;
  }

  const meetingClean =
    meetingSignal(
      meeting
    );

  const contactClean =
    normalizeAgentText(
      contact
    );

  if (
    !meetingClean
    || meetingClean ===
      contactClean
  ) {
    return contact;
  }

  return meeting;
}


export const resolveAgentContact =
  ({
    items = [],
    query = "",
  }) =>
    resolveItems({
      items,
      query,
      exactMatch:
        contactExact,
      searchable:
        searchableProspect,
    });


export const resolveAgentDocument =
  ({
    items = [],
    query = "",
  }) =>
    resolveItems({
      items,
      query,
      exactMatch:
        documentExact,
      searchable:
        searchableDocument,
    });


export const resolveAgentMeeting =
  ({
    items = [],
    meetingQuery = "",
    contactQuery = "",
  }) => {
    const query =
      normalizeAgentMeetingQuery({
        meetingQuery,
        contactQuery,
      });

    return resolveItems({
      items,
      query,
      exactMatch:
        meetingExact,
      searchable:
        searchableMeeting,
    });
  };


export const AGENT_ENTITY_RESOLVERS =
  Object.freeze({
    contact:
      resolveAgentContact,
    document:
      resolveAgentDocument,
    meeting:
      ({
        items = [],
        query = "",
      }) =>
        resolveAgentMeeting({
          items,
          meetingQuery:
            query,
        }),
  });


export function resolveAgentEntity({
  entity,
  items,
  query,
}) {
  const resolver =
    AGENT_ENTITY_RESOLVERS[
      entity
    ];

  if (!resolver) {
    return {
      status:
        AGENT_ENTITY_RESOLUTION_STATUS
          .NOT_FOUND,
      query:
        String(
          query || ""
        ),
      matches: [],
      selected: null,
      requiresConfirmation:
        false,
      error:
        `Resolver non enregistré: ${entity}`,
    };
  }

  return resolver({
    items,
    query,
  });
}
